import React, { useState } from 'react';
import {
    Box,
    Button,
    Container,
    Grid,
    Typography,
    Paper,
    IconButton,
    Chip,
    MenuItem,
    Select,
    FormControl,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import FilterListIcon from '@mui/icons-material/FilterList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
    ComposedChart,
} from 'recharts';

// ─── Chart Data ───────────────────────────────────────────────────────────────

const revenueTrendData = [
    { date: '01 May', current: 3800000, last: 3200000 },
    { date: '06 May', current: 7200000, last: 5800000 },
    { date: '11 May', current: 5100000, last: 6200000 },
    { date: '16 May', current: 8900000, last: 7100000 },
    { date: '21 May', current: 6400000, last: 5500000 },
    { date: '26 May', current: 9200000, last: 8000000 },
    { date: '31 May', current: 8100000, last: 7400000 },
];

const passengerTrendData = [
    { date: '01 May', current: 42000, last: 38000 },
    { date: '06 May', current: 65000, last: 55000 },
    { date: '11 May', current: 58000, last: 62000 },
    { date: '16 May', current: 78000, last: 70000 },
    { date: '21 May', current: 72000, last: 68000 },
    { date: '26 May', current: 85000, last: 75000 },
    { date: '31 May', current: 80000, last: 72000 },
];

const paymentChannelData = [
    { name: 'Cash', value: 31.6, color: '#1976d2' },
    { name: 'Card', value: 28.4, color: '#2e7d32' },
    { name: 'Mobile Payment', value: 22.7, color: '#5c35a0' },
    { name: 'Travel Card', value: 10.1, color: '#ed6c02' },
    { name: 'Online', value: 7.2, color: '#00acc1' },
];

const depotRevenueData = [
    { depot: 'Colombo Depot', revenue: 28650000 },
    { depot: 'Kandy Depot', revenue: 18750000 },
    { depot: 'Kurunegala Depot', revenue: 12450000 },
    { depot: 'Matara Depot', revenue: 9800000 },
    { depot: 'Galle Depot', revenue: 8650000 },
    { depot: 'Rathnapura Depot', revenue: 7250000 },
    { depot: 'Anuradhapura Depot', revenue: 6350000 },
    { depot: 'Negombo Depot', revenue: 5450000 },
    { depot: 'Jaffna Depot', revenue: 4150000 },
    { depot: 'Badulla Depot', revenue: 3060000 },
];

const tripsLoadData = [
    { date: '01 May', trips: 2100, loadFactor: 68 },
    { date: '06 May', trips: 3800, loadFactor: 74 },
    { date: '11 May', trips: 4200, loadFactor: 71 },
    { date: '16 May', trips: 3600, loadFactor: 78 },
    { date: '21 May', trips: 4800, loadFactor: 76 },
    { date: '26 May', trips: 4100, loadFactor: 80 },
    { date: '31 May', trips: 3900, loadFactor: 75 },
];

// ─── Stat cards data ──────────────────────────────────────────────────────────

const topCards = [
    { label: 'Total Revenue (MTD)', value: 'Rs. 124,560,850', change: '+12.5% vs Last Month', up: true, color: '#1976d2', bg: '#e3f2fd', icon: '₹' },
    { label: 'Total Passengers', value: '1,246,580', change: '+8.3% vs Last Month', up: true, color: '#2e7d32', bg: '#e8f5e9', icon: '👥' },
    { label: 'Non-Cash %', value: '68.4%', change: '+5.6% vs Last Month', up: true, color: '#5c35a0', bg: '#ede7f6', icon: '💳' },
    { label: 'Avg Revenue per Trip', value: 'Rs. 8,745', change: '+6.1% vs Last Month', up: true, color: '#ed6c02', bg: '#fff3e0', icon: '📊' },
];

const bottomCards = [
    { label: 'Load Factor %', value: '72.6%', change: '+4.3% vs Target (70%)', up: true, color: '#1976d2', bg: '#e3f2fd', icon: '📈' },
    { label: 'On-Time Performance %', value: '91.2%', change: '−2.1% vs Target (95%)', up: false, color: '#2e7d32', bg: '#e8f5e9', icon: '⏰' },
    { label: 'Failed Tap Rate %', value: '1.35%', change: '−0.18% vs Benchmark (1.50%)', up: false, color: '#d32f2f', bg: '#ffebee', icon: '❌' },
    { label: 'Total Revenue Leakage (Est.)', value: 'Rs. 2,345,780', change: '−6.7% vs Last Month', up: false, color: '#ed6c02', bg: '#fff3e0', icon: '🛡' },
];

const bottomStats = [
    { label: 'Total Trips', value: '14,568', change: '+7.8%', up: true },
    { label: 'Completed Trips', value: '13,842', change: '+7.4%', up: true },
    { label: 'Cancelled Trips', value: '368', change: '−5.6%', up: false },
    { label: 'Average Trip Distance', value: '96.3 km', change: '+2.3%', up: true },
    { label: 'Fleet Utilization %', value: '76.4%', change: '+4.1%', up: true },
    { label: 'Active Buses', value: '1,245', sub: 'of 1,632', change: '', up: true },
    { label: 'Active POS Devices', value: '1,087', sub: 'of 1,250', change: '', up: true },
];

const systemAlerts = [
    { icon: <WarningAmberIcon sx={{ color: '#d32f2f' }} />, label: 'Failed Tap Exceptions', sub: 'Today', count: 128, countColor: '#d32f2f' },
    { icon: <WarningAmberIcon sx={{ color: '#ed6c02' }} />, label: 'Breakdown Refunds Pending Approval', sub: 'Today', count: 23, countColor: '#ed6c02' },
    { icon: <InfoIcon sx={{ color: '#1976d2' }} />, label: 'POS Devices Offline', sub: 'Today', count: 18, countColor: '#1976d2' },
    { icon: <CheckCircleIcon sx={{ color: '#2e7d32' }} />, label: 'Scheduled Trips Completed', sub: 'Today', count: 1245, countColor: '#2e7d32' },
];

// ─── Reusable MiniSparkline placeholder (tiny inline trend) ──────────────────

const MiniLine = ({ color }) => (
    <Box sx={{ height: 40, mt: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={revenueTrendData}>
                <Line type="monotone" dataKey="current" stroke={color} strokeWidth={1.5} dot={false} />
            </LineChart>
        </ResponsiveContainer>
    </Box>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({ card }) => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box sx={{ backgroundColor: card.bg, borderRadius: 1.5, px: 1, py: 0.5, fontSize: 20 }}>
                {card.icon}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>{card.label}</Typography>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: card.color, lineHeight: 1.2, mb: 0.5 }}>
            {card.value}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {card.up
                ? <TrendingUpIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
                : <TrendingDownIcon sx={{ fontSize: 14, color: '#d32f2f' }} />}
            <Typography variant="caption" sx={{ color: card.up ? '#2e7d32' : '#d32f2f', fontWeight: 600 }}>
                {card.change}
            </Typography>
        </Box>
        <MiniLine color={card.color} />
    </Paper>
);

// ─── Component ────────────────────────────────────────────────────────────────

const Dashboard = () => {
    const [revPeriod, setRevPeriod] = useState('Daily');
    const [pasPeriod, setPasPeriod] = useState('Daily');
    const [tripPeriod, setTripPeriod] = useState('Daily');
    const [depotPeriod, setDepotPeriod] = useState('MTD');

    const PeriodSelect = ({ value, onChange }) => (
        <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select value={value} onChange={e => onChange(e.target.value)}
                sx={{ fontSize: 12, height: 28, '.MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' } }}>
                <MenuItem value="Daily">Daily</MenuItem>
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="MTD">MTD</MenuItem>
            </Select>
        </FormControl>
    );

    const fmtM = (v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v;
    const fmtK = (v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v;

    return (
        <Container component="main" maxWidth="full" sx={{ py: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>

                {/* ── Page Title ── */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Dashboard</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Button variant="outlined" startIcon={<CalendarTodayIcon />} endIcon={<KeyboardArrowDownIcon />}
                            sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, height: 38, fontSize: 13 }}>
                            01 May 2026 – 31 May 2026
                        </Button>
                        <Button variant="outlined" startIcon={<FilterListIcon />}
                            sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, height: 38 }}>
                            Filters
                        </Button>
                        <IconButton sx={{ position: 'relative' }}>
                            <NotificationsIcon />
                            <Box sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: '#d32f2f', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography sx={{ fontSize: 9, color: '#fff', fontWeight: 700 }}>8</Typography>
                            </Box>
                        </IconButton>
                    </Box>
                </Box>

                {/* ── All Stat Cards ── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',                 // mobile → 1 per row
                            sm: 'repeat(2, 1fr)',     // small → 2 per row
                            md: 'repeat(4, 1fr)',     // medium → 4 per row
                            lg: 'repeat(4, 1fr)',     // large → 4 per row
                        },
                        gap: 2,
                        width: '100%',
                        mb: 3
                    }}
                >
                    {[...topCards, ...bottomCards].map((card) => (
                        <StatCard key={card.label} card={card} />
                    ))}
                </Box>

                {/* ── Charts Row 1: Revenue Trend | Passenger Trend | Payment Channel Pie ── */}
                <Grid container sx={{ mb: 2, width: '100%' }}>

                    {/* Revenue Trend */}
                    <Grid item xs={12} md={4} sx={{ pr: { xs: 0, md: 2 } }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Revenue Trend</Typography>
                                <PeriodSelect value={revPeriod} onChange={setRevPeriod} />
                            </Box>
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={revenueTrendData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis tickFormatter={fmtM} tick={{ fontSize: 10 }} />
                                    <Tooltip formatter={(v) => `Rs. ${(v / 1000000).toFixed(2)}M`} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="current" name="Current Period (Rs.)" stroke="#1976d2" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="last" name="Last Month (Rs.)" stroke="#90caf9" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Passenger Trend */}
                    <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Passenger Trend</Typography>
                                <PeriodSelect value={pasPeriod} onChange={setPasPeriod} />
                            </Box>
                            <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={passengerTrendData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis tickFormatter={fmtK} tick={{ fontSize: 10 }} />
                                    <Tooltip formatter={(v) => `${(v / 1000).toFixed(1)}K`} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="current" name="Current Period" stroke="#2e7d32" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="last" name="Last Month" stroke="#a5d6a7" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Revenue by Payment Channel — Donut */}
                    <Grid item xs={12} md={4} sx={{ pl: { xs: 0, md: 2 } }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Revenue by Payment Channel</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ position: 'relative', width: 160, height: 160, flexShrink: 0 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={paymentChannelData} cx="50%" cy="50%" innerRadius={50} outerRadius={75}
                                                dataKey="value" paddingAngle={2}>
                                                {paymentChannelData.map((entry, i) => (
                                                    <Cell key={i} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(v) => `${v}%`} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555' }}>Rs.</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1976d2', lineHeight: 1 }}>124.56M</Typography>
                                        <Typography sx={{ fontSize: 10, color: '#888' }}>Total</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ ml: 1, flexGrow: 1 }}>
                                    {paymentChannelData.map((item) => (
                                        <Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: item.color, flexShrink: 0 }} />
                                                <Typography variant="caption">{item.name}</Typography>
                                            </Box>
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>{item.value}%</Typography>
                                        </Box>
                                    ))}
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>MTD Revenue Share</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── Charts Row 2: Depot Bar | Trips+Load | System Alerts ── */}
                <Grid container sx={{ mb: 3, width: '100%' }}>

                    {/* Revenue by Depot (Top 10) — Horizontal Bar */}
                    <Grid item xs={12} md={4} sx={{ pr: { xs: 0, md: 2 } }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Revenue by Depot (Top 10)</Typography>
                                <PeriodSelect value={depotPeriod} onChange={setDepotPeriod} />
                            </Box>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={depotRevenueData} layout="vertical"
                                    margin={{ top: 0, right: 40, left: 0, bottom: 0 }}>
                                    <XAxis type="number" tickFormatter={fmtM} tick={{ fontSize: 9 }} />
                                    <YAxis type="category" dataKey="depot" tick={{ fontSize: 9 }} width={110} />
                                    <Tooltip formatter={(v) => `Rs. ${(v / 1000000).toFixed(2)}M`} />
                                    <Bar dataKey="revenue" fill="#1976d2" radius={[0, 3, 3, 0]}
                                        label={{ position: 'right', formatter: (v) => `Rs. ${(v / 1000000).toFixed(2)}M`, fontSize: 9, fill: '#555' }} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* Trips vs Load Factor — Composed */}
                    <Grid item xs={12} md={4}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Trips vs Load Factor</Typography>
                                <PeriodSelect value={tripPeriod} onChange={setTripPeriod} />
                            </Box>
                            <ResponsiveContainer width="100%" height={260}>
                                <ComposedChart data={tripsLoadData} margin={{ top: 4, right: 20, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={fmtK} />
                                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} domain={[0, 100]} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar yAxisId="left" dataKey="trips" name="Trips" fill="#1976d2" radius={[3, 3, 0, 0]} />
                                    <Line yAxisId="right" type="monotone" dataKey="loadFactor" name="Load Factor %" stroke="#2e7d32" strokeWidth={2} dot={false} />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Paper>
                    </Grid>

                    {/* System Alerts (Live) */}
                    <Grid item xs={12} md={4} sx={{ pl: { xs: 0, md: 2 } }}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>System Alerts (Live)</Typography>
                                <Button size="small" sx={{ textTransform: 'none', color: '#1976d2', fontWeight: 600, p: 0 }}>View All</Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {systemAlerts.map((alert, i) => (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1.5, border: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {alert.icon}
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>{alert.label}</Typography>
                                                <Typography variant="caption" color="text.secondary">{alert.sub}</Typography>
                                            </Box>
                                        </Box>
                                        <Chip label={alert.count.toLocaleString()} size="small"
                                            sx={{ backgroundColor: alert.countColor, color: '#fff', fontWeight: 700, fontSize: 11, minWidth: 44 }} />
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ── Bottom Stats Bar ── */}
                <Paper variant="outlined" sx={{ width: '100%', borderRadius: 2, mb: 3 }}>
                    <Grid container>
                        {bottomStats.map((stat, i) => (
                            <Grid item xs={6} sm={4} md key={stat.label}
                                sx={{ p: 2, borderRight: i < bottomStats.length - 1 ? '1px solid #e0e0e0' : 'none', textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11, mb: 0.5 }}>{stat.label}</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{stat.value}</Typography>
                                {stat.sub && <Typography variant="caption" color="text.secondary">({stat.sub})</Typography>}
                                {stat.change && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, mt: 0.3 }}>
                                        {stat.up
                                            ? <TrendingUpIcon sx={{ fontSize: 12, color: '#2e7d32' }} />
                                            : <TrendingDownIcon sx={{ fontSize: 12, color: '#d32f2f' }} />}
                                        <Typography variant="caption" sx={{ color: stat.up ? '#2e7d32' : '#d32f2f', fontWeight: 600 }}>
                                            {stat.change}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

            </Box>
        </Container>
    );
};

export default Dashboard;